import CryptoJS from 'crypto-js';

const CALLBACK_HASH_SECRET_KEY =
  process.env.REACT_APP_SECRET_KEY ||
  'tryygl9awugdwaam0vvpw3n6bvhopabnxscle8jwj9odm4rbpr9fyqd7vy31dhjk';

function requireSecret() {
  if (!CALLBACK_HASH_SECRET_KEY) {
    throw new Error("HMAC Secret Key is missing in environment variables.");
  }
}

function omitUndefined(data) {
  const out = {};
  Object.keys(data || {}).forEach((key) => {
    if (data[key] !== undefined) out[key] = data[key];
  });
  return out;
}

export function createNewHmac(data) {
  requireSecret();

  const hashResp = CryptoJS.SHA256(CALLBACK_HASH_SECRET_KEY);
  const copy = omitUndefined(data);
  delete copy.hmac;
  delete copy.mac;
  delete copy.userHmac;

  if ('actions' in copy && Array.isArray(copy.actions)) {
    copy.actions = copy.actions
      .map((action) =>
        Object.keys(action)
          .sort()
          .map((key) => action[key])
          .join('')
      )
      .join('');
  }

  const hmacbaseRes = Object.keys(copy)
    .sort()
    .map((key) => copy[key])
    .join('');

  const hmacResp = CryptoJS.HmacSHA256(String(hmacbaseRes), hashResp);
  return CryptoJS.enc.Hex.stringify(hmacResp);
}

export function verifyHmac(payload) {
  requireSecret();
  const expected = createNewHmac(payload);
  return expected === payload.userHmac || expected === payload.hmac;
}

function pathnameFromUrl(url) {
  try {
    if (/^https?:\/\//i.test(url)) {
      return new URL(url).pathname;
    }
    return String(url || '').split('?')[0] || '/';
  } catch (e) {
    return String(url || '').split('?')[0] || '/';
  }
}

function makeNonce(time) {
  return `${time}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * Attach anti-replay HMAC fields. No-op if userHmac is already present.
 */
export function attachRequestHmac(config) {
  if (!config) return config;
  const headers = { ...(config.headers || {}) };
  if (headers.userHmac || headers.UserHmac) return config;

  const method = String(config.method || 'get').toUpperCase();
  const url = config.url || '';
  const path = pathnameFromUrl(url);
  const time = String(Date.now());
  const nonce = makeNonce(time);
  const token =
    headers.Authorization ||
    headers.authorization ||
    (typeof localStorage !== 'undefined'
      ? `Bearer ${localStorage.getItem('token')}`
      : '');

  if (method === 'GET' || method === 'HEAD' || method === 'DELETE') {
    if (!token || token === 'Bearer null' || token === 'Bearer ') {
      config.headers = headers;
      return config;
    }
    const payload = { token, time, nonce, path, method };
    headers.userHmac = createNewHmac(payload);
    headers['x-request-time'] = time;
    headers['x-request-nonce'] = nonce;
    config.headers = headers;
    return config;
  }

  if (method === 'POST' || method === 'PUT' || method === 'PATCH') {
    const data = config.data;
    // Multipart bodies are not parsed until multer; sign via headers like GET.
    if (typeof FormData !== 'undefined' && data instanceof FormData) {
      if (headers.userHmac || headers.UserHmac || data.get('userHmac')) {
        config.headers = headers;
        return config;
      }
      const payload = { token, time, nonce, path, method };
      headers.userHmac = createNewHmac(payload);
      headers['x-request-time'] = time;
      headers['x-request-nonce'] = nonce;
      config.headers = headers;
      return config;
    }

    // Drop undefined so JSON body matches what we sign (JSON omits undefined).
    let body =
      data && typeof data === 'object' ? omitUndefined(data) : data ? { value: data } : {};
    if (body.userHmac) {
      config.data = body;
      config.headers = headers;
      return config;
    }
    body.time = time;
    body.nonce = nonce;
    body.path = path;
    body.method = method;
    if (
      path === '/login' ||
      path.startsWith('/login/') ||
      path === '/admin/login' ||
      path.startsWith('/admin/login')
    ) {
      body.newTime = time;
    }
    body = omitUndefined(body);
    body.userHmac = createNewHmac(body);
    config.data = body;
    config.headers = headers;
    return config;
  }

  config.headers = headers;
  return config;
}
